import moment from "moment";
import { formatDateTime } from "../../../../utils";

const LAST_7_DAYS = "last7";
const FIRST_7_DAYS = "first7";
const LAST_7_DAYS_OF_CAMPAIGN = "last7Campaign";
const LAST_7_DAYS_FROM_CANCELLATION = "last7Cancellation";

const buildRange = (from, to) => {
  const displayName = `${formatDateTime(from, "DD MMM YYYY")} - ${formatDateTime(
    to,
    "DD MMM YYYY"
  )}`;
  return { from, to, displayName };
};

const getLast7Days = () => {
  const to = moment().startOf("day").toDate();
  const from = moment().subtract(6, "days").startOf("day").toDate();
  return buildRange(from, to);
};

const getDateRangeStrategy = (campaign) => {
  if (!campaign || !campaign.status) return LAST_7_DAYS;

  switch (campaign.status) {
    case "Planned":
      return FIRST_7_DAYS;
    case "Active":
      return LAST_7_DAYS;
    case "Completed":
    case "Paused":
      return LAST_7_DAYS_OF_CAMPAIGN;
    case "Cancelled":
      return LAST_7_DAYS_FROM_CANCELLATION;
    default:
      return LAST_7_DAYS;
  }
};

/**
 * Returns the default date range for Spot Status pages based on entity type and campaign state.
 *
 * Theater/Screen: always last 7 days.
 * Campaign pages: depends on campaign.status:
 *   Planned → first 7 days of campaign (validity.fromDate + 6 days)
 *   Active → last 7 days from today
 *   Completed/Paused → last 7 days of campaign (validity.toDate - 6 days to toDate)
 *   Cancelled → last 7 days from cancellation date (statusChanges entry)
 */
const getSpotStatusDefaultDateRange = (entityType, campaign) => {
  // Theatre/Screen: no campaign context, always last 7 days
  if (entityType === "theatre" || entityType === "screen" || !campaign) {
    return getLast7Days();
  }

  const strategy = getDateRangeStrategy(campaign);

  switch (strategy) {
    case FIRST_7_DAYS: {
      const campaignFrom = campaign.validity?.fromDate;
      if (!campaignFrom) return getLast7Days();
      const from = moment(campaignFrom, "YYYY-MM-DD").startOf("day").toDate();
      const to = moment(campaignFrom, "YYYY-MM-DD").add(6, "days").startOf("day").toDate();
      return buildRange(from, to);
    }

    case LAST_7_DAYS_OF_CAMPAIGN: {
      const campaignTo = campaign.validity?.toDate;
      if (!campaignTo) return getLast7Days();
      const to = moment(campaignTo, "YYYY-MM-DD").startOf("day").toDate();
      const from = moment(campaignTo, "YYYY-MM-DD").subtract(6, "days").startOf("day").toDate();
      return buildRange(from, to);
    }

    case LAST_7_DAYS_FROM_CANCELLATION: {
      let cancellationDate = null;
      if (campaign.statusChanges && campaign.statusChanges.length > 0) {
        const cancelEntries = campaign.statusChanges.filter((sc) => sc.status === "Cancelled");
        const cancelEntry = cancelEntries[cancelEntries.length - 1];
        if (cancelEntry && cancelEntry.fromDate) {
          cancellationDate = moment(cancelEntry.fromDate, "YYYY-MM-DD");
        }
      }
      if (!cancellationDate || !cancellationDate.isValid()) return getLast7Days();
      const to = cancellationDate.startOf("day").toDate();
      const from = moment(to).subtract(6, "days").startOf("day").toDate();
      return buildRange(from, to);
    }

    case LAST_7_DAYS:
    default:
      return getLast7Days();
  }
};

export default getSpotStatusDefaultDateRange;
